import type { OrgSummary } from "@cva/shared";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  CalendarDays,
  Check,
  ChevronsUpDown,
  FolderKanban,
  Images,
  LayoutTemplate,
  Lock,
  LogOut,
  Palette,
  Plus,
  Settings,
  ShieldCheck,
  Trash2,
  UserRound,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { orpc } from "../lib/api";
import { rememberOrg, signOut, useSession } from "../lib/session";
import { createOrganization } from "../routes/flows";
import { ActivityTray } from "./activity";
import { EnvBadge, Logo } from "./env-badge";
import { LegalLinks } from "./legal-links";
import { NotificationBell } from "./notifications";
import { SearchButton, SearchPalette, useSearchShortcut } from "./search-palette";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter } from "./ui/dialog";
import {
  DropdownContent,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSeparator,
  DropdownTrigger,
} from "./ui/dropdown";
import { Field, Input } from "./ui/input";

export function initials(name: string) {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function Avatar({
  name,
  className = "size-7 text-xs",
}: {
  name: string;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-brand-100 font-semibold text-brand-700 ${className}`}
    >
      {initials(name) || "?"}
    </span>
  );
}

function OrgSwitcher({ current, orgs }: { current?: OrgSummary; orgs: OrgSummary[] }) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownTrigger className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-zinc-100">
          <span className="flex size-8 items-center justify-center rounded-lg bg-brand-600 text-sm font-semibold text-white">
            {initials(current?.name ?? "?")}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-zinc-900">
              {current?.name ?? "Organisation"}
            </span>
            <span className="block text-xs text-zinc-500 capitalize">{current?.role}</span>
          </span>
          <ChevronsUpDown className="size-4 text-zinc-400" />
        </DropdownTrigger>
        <DropdownContent align="start" className="w-60">
          <DropdownLabel>Organisationen</DropdownLabel>
          {orgs.map((org) => (
            <DropdownItem
              key={org.id}
              onSelect={() => {
                rememberOrg(org.id);
                void navigate({ to: "/o/$orgId", params: { orgId: org.id } });
              }}
            >
              <span className="flex-1 truncate">{org.name}</span>
              {org.id === current?.id ? <Check className="text-brand-600" /> : null}
            </DropdownItem>
          ))}
          <DropdownSeparator />
          <DropdownItem onSelect={() => setCreating(true)}>
            <Plus /> Neue Organisation
          </DropdownItem>
        </DropdownContent>
      </DropdownMenu>
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent
          title="Neue Organisation"
          description="Jede Organisation hat eigene Projekte, Medien, Mitglieder und eigenen Speicher."
        >
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              setBusy(true);
              const orgId = await createOrganization(name);
              setBusy(false);
              if (orgId) {
                setCreating(false);
                setName("");
                await navigate({ to: "/o/$orgId", params: { orgId } });
              }
            }}
          >
            <Field label="Name">
              <Input required autoFocus value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <DialogFooter>
              <Button type="submit" loading={busy}>
                Anlegen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function NavLink({
  to,
  orgId,
  icon,
  children,
  exact,
}: {
  to: string;
  orgId: string;
  icon: ReactNode;
  children: ReactNode;
  exact?: boolean;
}) {
  return (
    <Link
      to={to}
      params={{ orgId }}
      activeOptions={{ exact }}
      className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 data-[status=active]:bg-brand-50 data-[status=active]:text-brand-700 [&_svg]:size-4"
    >
      {icon}
      {children}
    </Link>
  );
}

/** No running subscription: say why nothing can be changed, and where to book. */
function ReadOnlyBanner({ org }: { org: OrgSummary }) {
  return (
    <div className="border-b border-amber-200 bg-amber-50 px-8 py-3 text-sm text-amber-900">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-2">
        <Lock className="size-4 shrink-0" />
        <p className="min-w-0 flex-1">
          <span className="font-medium">Kein laufendes Abo.</span> Alles bleibt erhalten und lässt
          sich ansehen, Bearbeiten, Hochladen und Exportieren geht erst wieder mit einem Tarif.
          {org.role === "owner" ? null : " Buchen kann der Owner der Organisation."}
        </p>
        {org.role === "owner" ? (
          <Link
            to="/o/$orgId/settings"
            params={{ orgId: org.id }}
            search={{ tab: "plan" }}
            className="rounded-md bg-amber-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800"
          >
            Tarif wählen
          </Link>
        ) : null}
      </div>
    </div>
  );
}

export function AppShell({ orgId, children }: { orgId: string; children: ReactNode }) {
  const { data: session } = useSession();
  const orgs = useQuery(orpc.orgs.list.queryOptions());
  const current = orgs.data?.find((org) => org.id === orgId);
  const admin = useQuery({ ...orpc.admin.session.queryOptions(), staleTime: 60_000 });
  const openAlerts =
    (admin.data?.openAlerts?.warning ?? 0) + (admin.data?.openAlerts?.critical ?? 0);
  const [searching, setSearching] = useState(false);
  useSearchShortcut(setSearching);

  return (
    <div className="flex min-h-full">
      <aside className="sticky top-0 flex h-screen w-64 shrink-0 flex-col border-r border-zinc-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 pt-5 pb-4">
          <Link to="/o/$orgId" params={{ orgId }} className="rounded-md" title="Zu den Projekten">
            <Logo />
          </Link>
          <div className="flex items-center gap-1">
            <EnvBadge />
            <NotificationBell orgId={orgId} side="right" />
          </div>
        </div>
        <div className="px-2">
          <OrgSwitcher current={current} orgs={orgs.data ?? []} />
        </div>
        <div className="mt-3 px-2">
          <SearchButton onClick={() => setSearching(true)} />
        </div>
        <nav className="mt-4 flex-1 space-y-0.5 px-2">
          <NavLink to="/o/$orgId" orgId={orgId} icon={<FolderKanban />} exact>
            Projekte
          </NavLink>
          <NavLink to="/o/$orgId/templates" orgId={orgId} icon={<LayoutTemplate />}>
            Vorlagen
          </NavLink>
          <NavLink to="/o/$orgId/planning" orgId={orgId} icon={<CalendarDays />}>
            Planung
          </NavLink>
          <NavLink to="/o/$orgId/brand" orgId={orgId} icon={<Palette />}>
            Marke
          </NavLink>
          <NavLink to="/o/$orgId/media" orgId={orgId} icon={<Images />}>
            Mediathek
          </NavLink>
          <NavLink to="/o/$orgId/trash" orgId={orgId} icon={<Trash2 />}>
            Papierkorb
          </NavLink>
          <NavLink to="/o/$orgId/settings" orgId={orgId} icon={<Settings />}>
            Einstellungen
          </NavLink>
        </nav>
        {session ? (
          <div className="border-t border-zinc-100 p-2">
            <DropdownMenu>
              <DropdownTrigger className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-zinc-100">
                <span className="relative">
                  <Avatar name={session.user.name} />
                  {openAlerts > 0 ? (
                    <span
                      className={`absolute -top-0.5 -right-0.5 size-2.5 rounded-full ring-2 ring-white ${admin.data?.openAlerts?.critical ? "bg-red-600" : "bg-amber-400"}`}
                      title="Offene Warnungen im Betrieb"
                    />
                  ) : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{session.user.name}</span>
                  <span className="block truncate text-xs text-zinc-500">{session.user.email}</span>
                </span>
              </DropdownTrigger>
              <DropdownContent align="start" className="w-56">
                <DropdownItem onSelect={() => (window.location.href = "/account")}>
                  <UserRound /> Konto & verbundene Apps
                </DropdownItem>
                {admin.data?.isAdmin ? (
                  <DropdownItem onSelect={() => (window.location.href = "/admin")}>
                    <ShieldCheck /> Administration
                    {openAlerts > 0 ? (
                      <span
                        className={`ml-auto rounded-full px-1.5 text-[11px] font-semibold ${admin.data.openAlerts?.critical ? "bg-red-600 text-white" : "bg-amber-400 text-zinc-950"}`}
                      >
                        {openAlerts}
                      </span>
                    ) : null}
                  </DropdownItem>
                ) : null}
                <DropdownSeparator />
                <DropdownItem onSelect={() => void signOut()}>
                  <LogOut /> Abmelden
                </DropdownItem>
              </DropdownContent>
            </DropdownMenu>
          </div>
        ) : null}
        <LegalLinks className="px-2 pb-3" />
      </aside>
      <main className="min-w-0 flex-1">
        {current?.readOnly ? <ReadOnlyBanner org={current} /> : null}
        <div className="mx-auto max-w-6xl px-8 py-8">{children}</div>
      </main>
      <ActivityTray orgId={orgId} />
      <SearchPalette orgId={orgId} open={searching} onOpenChange={setSearching} />
    </div>
  );
}
